import { Fragment } from "react";
import { Menu, MenuButton, MenuItems, Transition } from "@headlessui/react";
import Icon from "hardcover-ui/components/Icon";
import classNames from "lib/classNames";
import { buttonTheme } from "hardcover-ui/theme/components/button";
import { menuItemsTheme } from "hardcover-ui/theme/components/menu";
import faChevron from "icons/solid/chevron-down.svg";
import arrowAZ from "icons/regular/arrow-down-a-z.svg";
import arrowZA from "icons/regular/arrow-down-z-a.svg";
import arrow19 from "icons/regular/arrow-down-1-9.svg";
import arrow91 from "icons/regular/arrow-down-9-1.svg";
import arrowSymbolUp from "icons/regular/arrow-down-triangle-square.svg";
import arrowSymbolDown from "icons/regular/arrow-down-square-triangle.svg";
import BookEditionSortListButton from "./BookEditionSortListButton";
import { GenerateEditionUrlParams } from "./BookEditionControls";


const sortLookup = {
  string: arrowAZ,
  string_desc: arrowZA,
  number: arrow19,
  number_desc: arrow91,
  symbol: arrowSymbolUp,
  symbol_desc: arrowSymbolDown,
};
function findIcon(sortType: string, reverse = false) {
  return sortLookup[`${sortType}${reverse ? "_desc" : ""}`];
}
export interface BookEditionOrderOption {
  title: string;
  fieldname: string;
  type: string;
  icon: string;
  defaultSortDirection: string;
}
const OrderOptions: BookEditionOrderOption[] = [
  {
    title: "Title",
    fieldname: "title",
    type: "string",
    icon: arrowAZ,
    defaultSortDirection: "asc",
  },
  {
    title: "Popularity",
    fieldname: "users_count",
    type: "number",
    icon: arrow19,
    defaultSortDirection: "desc",
  },
  {
    title: "Page Count",
    fieldname: "pages",
    type: "number",
    icon: arrow19,
    defaultSortDirection: "desc",
  },
  {
    title: "Audio Length",
    fieldname: "audio_seconds",
    type: "number",
    icon: arrow19,
    defaultSortDirection: "desc",
  },
  {
    title: "Release Date",
    fieldname: "release_date",
    type: "number",
    icon: arrow19,
    defaultSortDirection: "asc",
  },
  {
    title: "Data Score",
    fieldname: "score",
    type: "number",
    icon: arrow19,
    defaultSortDirection: "asc",
  }
]


interface BookEditionSortButtonParams {
  generateUrl: ({sortBy, sortDirection}: GenerateEditionUrlParams) => string;
  sortBy: string | null;
  sortDirection: string | null;
}

export default function BookEditionSortButton({
  generateUrl,
  sortBy,
  sortDirection
}: BookEditionSortButtonParams) {
  const currentSort = OrderOptions.find((option) => option.fieldname === sortBy);
  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <div>
            <MenuButton
              className={classNames(
                "gap-2",
                buttonTheme.size.sm,
                buttonTheme.variant.pill
              )}
            >
              <Icon
                size="lg"
                icon={findIcon(
                  currentSort.type,
                  sortDirection === "desc"
                )}
              />
              <span className="hidden lg:inline">Sort:</span>
              <span className="font-semibold flex items-center whitespace-nowrap">
                {currentSort.title}
              </span>
              <Icon icon={faChevron} />
            </MenuButton>
          </div>
          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems
              static
              className={classNames(
                "absolute z-100 origin-top-left left-0 mt-1 rounded-md shadow-xl focus:outline-none divide-y border-2 border-gray-300 dark:border-gray-600",
                menuItemsTheme.variant.primary
              )}
              style={{ width: "320px" }}
            >
              {OrderOptions.map((orderOption) => (
                <BookEditionSortListButton
                  active={sortBy === orderOption.fieldname}
                  generateUrl={generateUrl}
                  key={orderOption.title}
                  orderOption={orderOption}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                />
              ))}
            </MenuItems>
          </Transition>
        </>
      )}
    </Menu>
  );
}
